import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  TrendingUp,
  Info,
  CheckCircle,
} from "lucide-react";

const MaxProductSubarrayVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    { id: 1, name: "Standard Case", array: [2, 3, -2, 4] },
    { id: 2, name: "With Zero", array: [-2, 0, -1] },
    { id: 3, name: "All Negatives", array: [-2, -1] },
    { id: 4, name: "Includes two negatives", array: [2, 3, -2, -4] },
    { id: 5, name: "Mixed Numbers", array: [6, -3, -10, 0, 2] },
  ];

  // State setup
  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [array, setArray] = useState(defaultTestCases[0].array);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1200);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function maxProduct(nums):", indent: 0 },
    { line: "if nums is empty: return 0", indent: 1 },
    { line: "maxProd = nums[0]", indent: 1 },
    { line: "minProd = nums[0]", indent: 1 },
    { line: "result = nums[0]", indent: 1 },
    { line: "", indent: 0 },
    { line: "for i = 1 to n-1:", indent: 1 },
    { line: "num = nums[i]", indent: 2 },
    { line: "tempMax = max(num, num * maxProd, num * minProd)", indent: 2 },
    { line: "minProd = min(num, num * maxProd, num * minProd)", indent: 2 },
    { line: "maxProd = tempMax", indent: 2 },
    { line: "result = max(result, maxProd)", indent: 2 },
    { line: "", indent: 0 },
    { line: "return result", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (nums) => {
    const n = nums.length;
    if (n === 0)
      return [
        { step: 0, description: "Array is empty.", isFinal: true, result: 0 },
      ];

    const allSteps = [];
    let stepCount = 0;

    let result = nums[0];
    let maxProd = nums[0];
    let minProd = nums[0];

    const addStep = (desc, hl, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description: desc,
        highlightLine: hl,
        overallMaxProduct: result,
        maxProdEndingHere: maxProd,
        minProdEndingHere: minProd,
        ...details,
      });
    };

    addStep(
      `Initialize result, maxProd, and minProd with nums[0] (${nums[0]}).`,
      2,
      { currentIndex: 0 }
    );

    for (let i = 1; i < n; i++) {
      const num = nums[i];

      addStep(`Processing index ${i} (value ${num}).`, 6, { currentIndex: i });

      const tempMax = Math.max(num, num * maxProd, num * minProd);
      addStep(
        `Calculate temporary max: max(${num}, ${num * maxProd}, ${
          num * minProd
        }) = ${tempMax}.`,
        8,
        { currentIndex: i, tempMax }
      );

      minProd = Math.min(num, num * maxProd, num * minProd);
      addStep(
        `Update minProd: min(${num}, ${num * maxProd}, ${
          num * minProd
        }) = ${minProd}.`,
        9,
        { currentIndex: i, tempMax }
      );

      maxProd = tempMax;
      addStep(`Update maxProd to temporary max: ${maxProd}.`, 10, {
        currentIndex: i,
      });

      if (maxProd > result) {
        addStep(
          `Compare result (${result}) with maxProd (${maxProd}). New result found.`,
          11,
          { currentIndex: i }
        );
        result = maxProd;
      } else {
        addStep(
          `Compare result (${result}) with maxProd (${maxProd}). No change.`,
          11,
          { currentIndex: i }
        );
      }
    }

    addStep(`Finished. Maximum product is ${result}.`, 13, {
      isFinal: true,
      currentIndex: n - 1,
    });
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(array);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
    setHighlightedLine(newSteps.length > 0 ? newSteps[0].highlightLine : -1);
  }, [array]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(steps.length > 0 ? steps[0].highlightLine : -1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setArray(testCases[index].array);
  };

  const addCustomTestCase = () => {
    try {
      const nums = JSON.parse(`[${customInput}]`);
      if (!Array.isArray(nums) || nums.some(isNaN) || nums.length === 0) {
        throw new Error("Input must be a non-empty array of numbers.");
      }
      const newTestCase = { id: Date.now(), name: `Custom`, array: nums };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert(`Invalid format: ${error.message}. Use comma-separated numbers.`);
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 600) return "Fast";
    if (speed <= 1200) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || {};

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <TrendingUp className="text-blue-500" /> Maximum Product Subarray
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given an integer array `nums`, find a contiguous non-empty
                subarray within the array that has the largest product, and
                return the product.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      [{tc.array.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 5 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Array: 2,3,-2,4"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-4 relative">
              <div className="flex flex-wrap gap-2">
                {array.map((num, idx) => (
                  <div key={idx} className="text-center">
                    <div
                      className={`w-14 h-14 flex items-center justify-center border-2 rounded font-mono text-lg font-bold transition-all duration-300
                        ${
                          idx === currentStepData.currentIndex
                            ? "scale-110 ring-2 ring-blue-500"
                            : "bg-gray-100 border-gray-300"
                        }`}
                    >
                      {num}
                    </div>
                    <span className="text-xs text-gray-500 mt-1">{idx}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
              <div className="p-2 rounded bg-blue-100">
                <div className="text-xs text-blue-800 font-medium">
                  Max Product Ending Here
                </div>
                <div className="font-bold text-lg text-blue-900">
                  {currentStepData.maxProdEndingHere ?? "-"}
                </div>
              </div>
              <div className="p-2 rounded bg-red-100">
                <div className="text-xs text-red-800 font-medium">
                  Min Product Ending Here
                </div>
                <div className="font-bold text-lg text-red-900">
                  {currentStepData.minProdEndingHere ?? "-"}
                </div>
              </div>
              <div className="p-2 rounded bg-green-100">
                <div className="text-xs text-green-800 font-medium">
                  Overall Max Product
                </div>
                <div className="font-bold text-lg text-green-900">
                  {currentStepData.overallMaxProduct ?? "-"}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              This problem is an extension of Kadane's algorithm. Because
              negative numbers can flip the largest product to the smallest and
              vice-versa, we must track both the maximum product and the minimum
              product of a subarray ending at the current position. For each
              element, the new max product is the maximum of the element itself,
              the element times the previous max product, or the element times
              the previous min product.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(1)
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The magic is in tracking the minimum product. A large negative
                  `minProd` can become a large positive `maxProd` when
                  multiplied by another negative number.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  If a zero is encountered, it acts as a reset point, as any
                  subarray including it will have a product of zero. The
                  algorithm handles this naturally.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The solution requires only a single pass and constant extra
                  space.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaxProductSubarrayVisualizer;
